import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { AccountingReportCycleTotal } from "@common/constants";
import { schedulesReportContract } from "@common/contracts/schedules-report.contract";
import { ReportFormat } from "@common/helpers/reports";
import {
    SchedulesReportDateBoundsQuery,
    SchedulesReportQuery,
} from "@common/schemas/domains/accounting/schedules-report.schema";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { getBackendAPIClient } from "@lib/backendAPIClient";
import { AccountingReportFilters } from "@components/accounting/types";
import { SchedulesReportParams } from "@components/accounting/schedulesReport/schedulesReportTypes";

const schedulesReportClient = getBackendAPIClient(schedulesReportContract);
const schedulesReportExportClient = getBackendAPIClient(
    schedulesReportContract,
    "blob",
);

export const SCHEDULES_REPORT_QUERY_KEY = "schedules-report";
export const SCHEDULES_REPORT_DATE_BOUNDS_QUERY_KEY =
    "schedules-report-date-bounds";

export function prepareSchedulesReportQuery(
    filters: AccountingReportFilters,
    params: SchedulesReportParams,
): SchedulesReportQuery | undefined {
    const { entityId, startDate, endDate, ledgerId } = filters;
    if (!startDate || !endDate || !entityId || !ledgerId) {
        return undefined;
    }
    return {
        startDate: formatISO(startDate, { representation: "date" }),
        endDate: formatISO(endDate, { representation: "date" }),
        entityId,
        entityIds: filters.entityIds?.length ? filters.entityIds : undefined,
        ledgerId,
        cycle:
            filters.cycle === AccountingReportCycleTotal.TOTAL
                ? undefined
                : filters.cycle,
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        ...params,
    };
}

export function useSchedulesReportQuery(
    filters: AccountingReportFilters,
    params: SchedulesReportParams,
) {
    const preparedQuery = prepareSchedulesReportQuery(filters, params);
    return useQuery({
        queryKey: [SCHEDULES_REPORT_QUERY_KEY, preparedQuery],
        enabled: preparedQuery !== undefined,
        queryFn: async () => {
            if (!preparedQuery) {
                return undefined;
            }
            const response = await schedulesReportClient.getSchedulesReport({
                query: preparedQuery,
            });
            if (response.status !== 200) {
                throw new Error("Failed to load schedules report");
            }
            return response.body;
        },
        placeholderData: keepPreviousData,
    });
}

interface ExportSchedulesReportParams {
    filters: AccountingReportFilters;
    params: SchedulesReportParams;
    format: ReportFormat;
    rowVisibility?: ReportSettingRowVisibility;
}

export async function exportSchedulesReport({
    filters,
    params,
    format,
    rowVisibility,
}: ExportSchedulesReportParams) {
    const query = prepareSchedulesReportQuery(filters, params);
    if (!query) {
        return undefined;
    }
    return await schedulesReportExportClient.exportSchedulesReport({
        query: { ...query, format, rowVisibility },
    });
}

export function useSchedulesReportDateBoundsQuery(
    filters: Pick<
        AccountingReportFilters,
        "entityId" | "entityIds" | "ledgerId" | "classes" | "classFilterType"
    >,
    params: SchedulesReportParams,
    options: { isEnabled?: boolean } = {},
) {
    const query: SchedulesReportDateBoundsQuery | undefined =
        filters.entityId && filters.ledgerId
            ? {
                  entityId: filters.entityId,
                  entityIds: filters.entityIds?.length
                      ? filters.entityIds
                      : undefined,
                  ledgerId: filters.ledgerId,
                  classes: filters.classes,
                  classFilterType: filters.classFilterType,
                  revenueSubMode: params.revenueSubMode,
                  method: params.method,
                  templateIds: params.templateIds,
                  scheduleApplicationIds: params.scheduleApplicationIds,
                  counterpartyIds: params.counterpartyIds,
                  includeUnassignedCounterparty:
                      params.includeUnassignedCounterparty,
                  hasCounterparty: params.hasCounterparty,
                  holdingAccountIds: params.holdingAccountIds,
                  groups: params.groups,
                  termStartFrom: params.termStartFrom,
                  termStartTo: params.termStartTo,
                  termEndFrom: params.termEndFrom,
                  termEndTo: params.termEndTo,
              }
            : undefined;
    return useQuery({
        queryKey: [SCHEDULES_REPORT_DATE_BOUNDS_QUERY_KEY, query],
        enabled: query !== undefined && options.isEnabled !== false,
        queryFn: async () => {
            if (!query) {
                return undefined;
            }
            const response =
                await schedulesReportClient.getSchedulesReportDateBounds({
                    query,
                });
            if (response.status !== 200) {
                throw new Error("Failed to load schedule date bounds");
            }
            return response.body;
        },
    });
}
